import { Injectable } from '@angular/core';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { DialogComponent } from '@shared/dialog/dialog.component';
import * as Constants from '@shared/_constants';
import { StudyResult } from '@models/studyResult.model';
import { StudySearchFilter } from '@models/studySearchFilter.model';
import { EliminationMatrixResultView } from '@models/eliminationMatrixResultView';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { SectionType, StudyAnalysisSteps, ReportTemplates, KeywordType } from '../mystudies/_study_constants';
import { DialogNotificationsComponent } from '@shared/dialog-notifications/dialog-notifications.component';
import { SubsetModel } from '@models/subset.model';
import { ReasonDialogComponent } from '@shared/reason-dialog/reason-dialog.component';
import { InternalNotesModalComponent } from '../mystudies/analysis/master-matrix/internalnotes-modal/internalnotes-modal.component';
import { KeywordExtractionService } from './keyword-extraction.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { AddKeywordsDialogComponent } from '../mystudies/analysis/keyword-screening/add-keywords-dialog/add-keywords-dialog.component';
import { StudyBlockUiService } from '@shared/study-block-ui/study-block-ui.service';

@Injectable()
export class DialogService {
  constructor(
    private config: NgbModalConfig,
    private modalService: NgbModal,
    private studyBlockUIService: StudyBlockUiService,
  ) {
    config.backdrop = 'static';
    config.keyboard = false;
  }

  public closeAll() {
    this.modalService.dismissAll();
  }

  public confirm(title?: string, message?: string, yesButtonText?: string, noButtonText?: string): Promise<Constants.ConfirmationResult> {
    this.studyBlockUIService.unblockUI();
    const modalRef = this.modalService.open(DialogComponent, {
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.title = title ? title : 'Discard Changes?';
    modalRef.componentInstance.message = message ? message : Constants.ConfirmationMessage.ConfirmDiscardChanges;
    modalRef.componentInstance.yesButtonText = yesButtonText ? yesButtonText : 'Yes';
    modalRef.componentInstance.noButtonText = noButtonText ? noButtonText : 'No';
    modalRef.componentInstance.showYesNo = true;

    return modalRef.result;
  }

  public acknowledge(title?: string, message?: string, okButtonText?: string): Promise<Constants.AcknowledgeResult> {
    this.studyBlockUIService.unblockUI();
    const modalRef = this.modalService.open(DialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.title = title ? title : 'Changes Discarded';
    modalRef.componentInstance.message = message ? message : Constants.ConfirmationMessage.AcknowledgeDiscardChanges;
    modalRef.componentInstance.okButtonText = okButtonText ? okButtonText : 'OK';
    modalRef.componentInstance.showOK = true;

    return modalRef.result;
  }
  public acknowledgeInterestRate(title?: string, message?: string, okButtonText?: string): Promise<Constants.AcknowledgeResult> {
    this.studyBlockUIService.unblockUI();
    const modalRef = this.modalService.open(DialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'sm',
    });
    modalRef.componentInstance.title = title ? title : 'Changes Discarded';
    modalRef.componentInstance.message = message ? message : Constants.ConfirmationMessage.AcknowledgeDiscardChanges;
    modalRef.componentInstance.okButtonText = okButtonText ? okButtonText : 'OK';
    modalRef.componentInstance.showOK = true;

    return modalRef.result;
  }
  public launchNewStudy(content: any, studyId: number = null, sourceStudyIsLegacy = false): Promise<StudyResult> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
      size: 'lg',
    });

    modalRef.componentInstance.leveragedStudyId = studyId;
    modalRef.componentInstance.sourceStudyIsLegacy = sourceStudyIsLegacy;

    return modalRef.result;
  }

  public launchNotifications() {
    const modalRef = this.modalService.open(DialogNotificationsComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'lg',
    });

    return modalRef.result;
  }

  public launchAddFilter(
    content: any,
    readOnly: boolean,
    strategy: StudySearchFilter = null,
    searchStrategyUpdateBeforeChange: any = null,
  ): Promise<void> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in filters-modal',
      centered: false,
      size: 'lg',
    });

    modalRef.componentInstance.readOnly = readOnly;

    if (strategy != null) {
      modalRef.componentInstance.searchFilterId = strategy.studySearchFilterId;
    }
    modalRef.componentInstance.searchStrategyUpdateBeforeChange = searchStrategyUpdateBeforeChange;
    return modalRef.result;
  }

  public launchPrivacyNotice(content: any): Promise<Constants.ConfirmationResult> {
    const modalRef = this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });

    return modalRef.result;
  }

  public launchKeywordScreeningInfo(content: any) {
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });
  }

  public launchDisplayDialog(
    content: any,
    parentStep: StudyAnalysisSteps,
    eliminationMatrixViewresult: EliminationMatrixResultView,
    searchStrategyId: number,
    readOnly: boolean,
    studySettingId: number,
    sectionType: SectionType,
  ): Promise<EliminationMatrixResultView> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in elimination-matrix-details-modal',
      centered: false,
    });

    modalRef.componentInstance.parentStep = parentStep;
    modalRef.componentInstance.sectionType = sectionType;
    modalRef.componentInstance.studySettingId = studySettingId;
    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.studySearchStrategyId = searchStrategyId;
    modalRef.componentInstance.eliminationMatrixViewresult = eliminationMatrixViewresult;

    return modalRef.result;
  }

  public launchEliminationCriertiaDialog(
    content: any,
    searchStrategyId: number,
    showInMasterMatrix: boolean,
  ): Promise<BDMatrixEliminationCategory[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
      size: 'lg',
    });

    modalRef.componentInstance.searchStrategyId = searchStrategyId;
    modalRef.componentInstance.showInMasterMatrix = showInMasterMatrix;

    return modalRef.result;
  }

  public launchSubsetDialog(content: any, screenType: string, allSubsets: SubsetModel[], subsetIds: number[]): Promise<SubsetModel> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.screenType = screenType;
    modalRef.componentInstance.allSubsets = allSubsets;
    modalRef.componentInstance.subsetIds = subsetIds;

    return modalRef.result;
  }

  public launchFinancialExportDialog(content: any, readOnly: boolean, exportAll: boolean, exportFromSS = false): Promise<any[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.exportAll = exportAll;
    modalRef.componentInstance.isExportFromSearchstrategy = exportFromSS;

    return modalRef.result;
  }

  launchReportDialog(content: any, template: ReportTemplates, options: any): Promise<void> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
      size: 'lg',
    });

    // variables defined in study-reports-modal\study-reports-modal.component.ts
    modalRef.componentInstance.reportTemplate = template;
    modalRef.componentInstance.reportOptions = options;

    return modalRef.result;
  }

  launchSlideOut(content: any): Promise<any[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in study-slide-out',
      centered: false,
    });

    return modalRef.result;
  }

  public launchFinancialImportDialog(content: any, readOnly: boolean): Promise<any[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.readOnly = readOnly;

    return modalRef.result;
  }

  public launchMatrixImportDialog(content: any, readOnly: boolean, title: string, studyAnalysisSteps: StudyAnalysisSteps, lastImportedDate: Date = null): Promise<any[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.studyAnalysisSteps = studyAnalysisSteps;
    modalRef.componentInstance.lastImportedDate = lastImportedDate;

    return modalRef.result;
  }

  public launchScrapeSettingsDialog(content: any, readOnly: boolean, studyId: number): Promise<any[]> {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.studyId = studyId;

    return modalRef.result;
  }

  public launchReasonDialog(): Promise<string> {
    const modalRef = this.modalService.open(ReasonDialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
    });

    return modalRef.result;
  }

  public launchManageScreenshots(content: any, readOnly: boolean, url: string, comparableId: number) {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      size: 'lg',
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.url = url;
    modalRef.componentInstance.comparableId = comparableId;

    return modalRef.result;
  }

  public launchInternalNotesDialog(readOnly: boolean, internalnotes: string) {
    const modalRef = this.modalService.open(InternalNotesModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      size: 'lg',
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.internalnotes = internalnotes;

    return modalRef.result;
  }

  public launchAddKeywordDialog(readOnly: boolean, keywordTypeId: KeywordType, keywordExtractionService: KeywordExtractionService) {
    const modalRef = this.modalService.open(AddKeywordsDialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      size: 'lg',
    });

    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.keywordTypeId = keywordTypeId;
    modalRef.componentInstance.keywordExtractionService = keywordExtractionService;

    return modalRef.result;
  }

  public launchBulkRejectDialog(
    content: any,
    eliminationCriteriaColumns: BDMatrixEliminationCategory[],
    sectionType: SectionType,
    showPredictionSection = false,
    showMixedPredictionsWarning = false,
    isBulkAccept = false,
    showNaPredictionInBatchMessage = false,
    comparablesWithNoPredictionsOnly = false,
    readOnly = false,
  ) {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      size: 'lg',
    });
    modalRef.componentInstance.predictionSectionVisible = showPredictionSection;
    modalRef.componentInstance.isBulkAccept = isBulkAccept;
    modalRef.componentInstance.showMixedPredictionsWarning = showMixedPredictionsWarning;
    modalRef.componentInstance.showNaPredictionInBatchMessage = showNaPredictionInBatchMessage;
    modalRef.componentInstance.eliminationCriteriaColumns = eliminationCriteriaColumns;
    modalRef.componentInstance.comparablesWithNoPredictionsOnly = comparablesWithNoPredictionsOnly;
    modalRef.componentInstance.readOnly = readOnly;

    modalRef.componentInstance.sectionType = sectionType;

    return modalRef.result;
  }

  public lunchPageTitlesRetrievalDialog(content: any, studyId: number, searchStrategyId: number) {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
    });

    modalRef.componentInstance.studyId = studyId;
    modalRef.componentInstance.studySearchStrategyId = searchStrategyId;
    return modalRef.result;
  }

  public launchNewStudyNotice(content: any): Promise<Constants.ConfirmationResult> {
    const modalRef = this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });

    return modalRef.result;
  }

  public launchScreenVersionNotice(content: any): Promise<Constants.ConfirmationResult> {
    const modalRef = this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });

    return modalRef.result;
  }

  public async logoutConfirmation() {
    const message = `${Constants.ConfirmationMessage.ConfirmLogout}`;

    const confirmationResult = await this.confirm('Please Confirm', message, 'Continue', 'Cancel');
    if (confirmationResult === Constants.ConfirmationResult.yes) {
      return true;
    }
    return false;
  }

  public async logoutConfirmationUnsavedChanges() {
    const message = `${Constants.ConfirmationMessage.ConfirmLogoutUnsavedChanges}`;

    const confirmationResult = await this.confirm('Please Confirm', message, 'Yes', 'No');
    if (confirmationResult === Constants.ConfirmationResult.yes) {
      return true;
    } else {
      return false;
    }
  }

  public async launchUnsavedChangesConfirmationDialog(logoutInProgress, warningMessage) {
    if (logoutInProgress) {
      return await this.logoutConfirmationUnsavedChanges();
    }

    const message = warningMessage;
    const confirmationResult = await this.confirm('Please Confirm', message, 'Yes', 'No');

    return confirmationResult === Constants.ConfirmationResult.yes ? true : false;
  }

  public launchFinancialTypeInfoDialog(content: any) {
    const modalRef = this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });
    return modalRef.result;
  }

  public launchConfigurationSettingDialog(content: any, studysearchstrategyId = 0) {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    });

    modalRef.componentInstance.studysearchstrategyId = studysearchstrategyId;

    return modalRef.result;
  }

  launchDialog(
    content: any,
    dialogOptions: any = {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      centered: false,
    },
  ): NgbModalRef {
    const modalRef = this.modalService.open(content, dialogOptions);
    return modalRef;
  }

  public launchShareholderOwnershipValidationDialog(
    content: any,
    sectionType: SectionType,
    searchStrategyId: number,
    shareholderIndependencyThreshold: number,
    shareholderIndependencyCriteria: number,
    readOnly = false,
  ) {
    const modalRef = this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'right in',
      size: 'lg',
    });
    modalRef.componentInstance.searchStrategyId = searchStrategyId;
    modalRef.componentInstance.shareholderIndependencyThreshold = shareholderIndependencyThreshold;
    modalRef.componentInstance.shareholderIndependencyCriteria = shareholderIndependencyCriteria;
    modalRef.componentInstance.readOnly = readOnly;
    modalRef.componentInstance.sectionType = sectionType;

    return modalRef.result;
  }
}
